import React, { useMemo, useState } from "react";

import {
  Badge,
  Box,
  Button,
  Card,
  Cell,
  Dropdown,
  Layout,
  MessageModalLayout,
  Modal,
  Page,
  Pagination,
  Search,
  Table,
  TableToolbar,
  Text,
  Tooltip,
  Loader,
  WixDesignSystemProvider,
  IconButton,
  TableColumn,
  Divider,
  CustomModalLayout,
  FormField,
  Tabs,
  accordionItemBuilder,
  AutoComplete,
} from "@wix/design-system";
import "@wix/design-system/styles.global.css";
import * as Icons from "@wix/wix-ui-icons-common";

import {
  useWixCollections,
  useWixProducts,
  useWixProductsCount,
  useWixProductsImport,
} from "../../../hooks/wix/useWix";
import { withProviders } from "../../withProviders";
import {
  addCollectionAndTransform,
  addExpandProperties,
  mergeArrays,
  truncateText,
} from "../../../services/utils/productUtils";
import WixProductRowDetail from "../../../components/features/products/wix/WixProductRowDetail";
import "../index.css";
import { useDebouncedCallback } from "use-debounce";
import WixLogo from "../wix_logo.svg";
import FiareLogo from "../fiare_logo.svg";
import EmptyTable from "../../../components/common/Table/EmptyTable";
import { visibilityStatusOptions } from "../../../config/constants/products/wixProduct.constant";
import { IWixProduct } from "../../../services/models/types/wixProduct.types";
import SiteContext from "../../../contexts/site-context";
import WixProduct from "../../../models/wix-product";
import { showToast } from "@wix/dashboard-sdk";
import Loading from "../../../components/common/Loader";
import Picture from "../../images/no-image.png";
import FaireProduct from "../../../models/faire-product";
import ExportModal from "../../../components/common/modal/ExportModal";
import { useTableItemSelection } from "../../../hooks/useTableItemSelection";
import SidePanelModal from "../../../components/common/modal/SidePanelModal";
import TrialExpiryAlert from "../../../components/trial-expiry-alert";
import {
  usePusherBulkExportProgress,
  usePusherExportProgress,
  usePusherImportProgress,
} from "../../../hooks/usePusher";
import useCategoryFetching from "../../../hooks/faire/useFaire";
import SyncedDate from "../../../components/common/SyncedDate";
import SimilarSkuExists from "../../../components/warnings/SimilarSkuExists";
import { getExportToFaireErrorMsg } from "../../../services/utils/errorUtils";

const Index: React.FC = () => {
  /*
    |--------------------------------------------------------------------------
    | Delete from faire
    |--------------------------------------------------------------------------
    |
    */
  const [productIdToBeDeletedFromFaire, setProductIdToBeDeletedFromFaire] = React.useState("");
  const [showDeleteFromFaireConfirmation, setShowDeleteFromFaireConfirmation] = React.useState(false);
  React.useEffect(() => {
    if (productIdToBeDeletedFromFaire !== "") {
      setShowDeleteFromFaireConfirmation(true);
    }
  }, [productIdToBeDeletedFromFaire]);

  const deleteFromFaire = () => {
    FaireProduct.delete(productIdToBeDeletedFromFaire)
      .then((response: any) => {
        showToast({
          type: "success",
          message: response.message,
        });
        setExportedData((prevData: any) => prevData - 1);
        setNotExportedData((previousData: any) => previousData + 1);
      })
      .catch((error) => {
        showToast({
          type: "error",
          message: error.message,
        });
      })
      .finally(() => {
        wixProductQuery.refetch();
        setProductIdToBeDeletedFromFaire("");
      });
  };

  /*
  |--------------------------------------------------------------------------
  | Product Export Status
  |--------------------------------------------------------------------------
  |
  */
  const [name, setName] = React.useState<string | number>("");
  const wixCollectionsQuery = useWixCollections({ name });
  const [exportStatus, setExportStatus] = React.useState<string | number>("");

  /*
    |--------------------------------------------------------------------------
    | Filter and Retrieve Products
    |--------------------------------------------------------------------------
    |
    */
  const wixProductsImportMutation = useWixProductsImport();
  const [productPage, setProductPage] = React.useState(1);
  const [visibility, setVisibility] = React.useState<string | number>("");
  const [keywords, setKeywords] = React.useState("");
  const [collectionId, setCollectionId] = React.useState<string | number>("");
  const wixProductQuery = useWixProducts({
    page: productPage,
    visibility,
    keywords,
    collectionId,
    withVariants: 1,
    exportStatus: exportStatus,
  });

  const [showConfirmationForImport, setShowConfirmationForImport] = React.useState(false);

  /*
  |--------------------------------------------------------------------------
  | Manage Products Count
  |--------------------------------------------------------------------------
  |
  */
  const {
    data: wixProductsCount,
    isSuccess,
    isFetching: isCurrentlyFetching,
    refetch: wixProductCountRefetch,
  } = useWixProductsCount();
  const countData = isSuccess && !isCurrentlyFetching ? wixProductsCount?.data : null;
  const [exportedData, setExportedData] = React.useState<number | null>(null);
  const [notExportedData, setNotExportedData] = React.useState<number | null>(null);

  React.useEffect(() => {
    if (isSuccess && !isCurrentlyFetching && wixProductsCount?.data) {
      const { exported_products, not_exported_products } = wixProductsCount?.data;
      setExportedData(exported_products);
      setNotExportedData(not_exported_products);
    }
  }, [isSuccess, isCurrentlyFetching, wixProductsCount]);

  /*
  |--------------------------------------------------------------------------
  | Count Display Conditions
  |--------------------------------------------------------------------------
  |
  */
  const allProductCountCondition = countData?.all_products && countData?.all_products !== 0;
  const importedProductCountCondition = countData?.imported_products && countData?.imported_products !== 0;

  /*
  |--------------------------------------------------------------------------
  | Status Option
  |--------------------------------------------------------------------------
  |
  */
  const productStatusOptions = [
    {
      id: "",
      title: (
        <Tooltip maxWidth={360} content="Here is the list of all your products currently in the Store.">
          <Text>
            All Products
            {allProductCountCondition ? ` (${countData?.all_products})` : ""}
          </Text>
        </Tooltip>
      ),
      maxWidth: 158,
    },
    {
      id: "IMPORTED",
      title: (
        <Tooltip
          maxWidth={360}
          content="Here is the list of products that have been imported from your Faire store into Wix store"
        >
          <Text>
            Imported
            {importedProductCountCondition ? ` (${countData?.imported_products})` : ""}
          </Text>
        </Tooltip>
      ),
    },
    {
      id: "EXPORTED",
      title: (
        <Tooltip
          maxWidth={360}
          content="Here is the list of your products that have already been exported to your Faire store. Please click on ‘Delete from Faire’ to remove these products from your Faire store."
        >
          <Text>
            Exported
            {exportedData && exportedData !== 0 ? ` (${exportedData})` : ""}
          </Text>
        </Tooltip>
      ),
    },
    {
      id: "NOT_EXPORTED",
      title: (
        <Tooltip
          maxWidth={360}
          content="Here is the list of your products that are not in your Faire store. Please click on ‘Export to Faire’  to send these products to your Faire store."
        >
          <Text>
            Not Exported
            {notExportedData && notExportedData !== 0 ? ` (${notExportedData})` : ""}
          </Text>
        </Tooltip>
      ),
    },
  ];

  /*
  |--------------------------------------------------------------------------
  | Manage Side Panel
  |--------------------------------------------------------------------------
  |
  */
  const [showSidePanel, setShowSidePanel] = React.useState(false);

  const openPanel = (event: any) => setShowSidePanel(true); // Open Side Panel
  const closePanel = () => setShowSidePanel(false); // Close Side Panel

  /*
  |--------------------------------------------------------------------------
  | Handle Tab Changes
  |--------------------------------------------------------------------------
  |
  */
  const handleTabChange = (value: any) => {
    setExportStatus(value.id);
    setProductPage(1);
  };

  /*
    |--------------------------------------------------------------------------
    | Upload to faire
    |--------------------------------------------------------------------------
    |
    */

  const [showConirmationForUploadToFaire, setShowConfirmationForUploadToFaire] = React.useState<boolean>(false);
  const [selectedProductId, setSelectedProductId] = React.useState<string>("");
  const [uploadingToFaire, setUploadingToFaire] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (selectedProductId !== "") {
      setShowConfirmationForUploadToFaire(true);
    }
  }, [selectedProductId]);

  /*
    |--------------------------------------------------------------------------
    | Handle upload to faire
    |--------------------------------------------------------------------------
    |
    */

  const handleUploadToFaire = (productId: string) => {
    setSelectedProductId(productId);
  };

  /*
    |--------------------------------------------------------------------------
    | Upload to faire cancellation
    |--------------------------------------------------------------------------
    |
    */

  const handleCancelUploadToFaire = () => {
    setSelectedProductId("");
  };

  /*
    |--------------------------------------------------------------------------
    | Upload To Faire Confirmation
    |--------------------------------------------------------------------------
    |
    */

  const handleConfirmUploadToFaire = () => {
    checkCategorySelected();
  };

  /*
    |--------------------------------------------------------------------------
    | Upload To Faire
    |--------------------------------------------------------------------------
    |
    */

  const uploadToFaire = () => {
    if (uploadingToFaire) return;
    setUploadingToFaire(true);
    WixProduct.uploadToFaire(selectedProductId, categoryId)
      .then((response: any) => {
        showToast({
          type: "success",
          message: response.message,
        });
      })
      .catch((error) => {
        showToast({
          type: "error",
          message: error.message,
        });
      })
      .finally(() => {
        wixProductQuery.refetch();
        setUploadingToFaire(false);
        setSelectedProductId("");
        setShowConfirmationForUploadToFaire(false);
      });
  };

  /*
  |--------------------------------------------------------------------------
  | Category Fetch
  |--------------------------------------------------------------------------
  |
  */
  const {
    setKeywords: setCategoryKeywords,
    fetchCategories,
    keywords: categoryKeywords,
    hasMore,
    debouncedCategory,
    faireCategoryOptions,
  } = useCategoryFetching();

  // Category Options
  const options = [
    {
      id: "0",
      value: "Select Category",
    },
    ...faireCategoryOptions,
  ];

  /*
  |--------------------------------------------------------------------------
  | Handle export
  |--------------------------------------------------------------------------
  |
  */

  const [isExporting, setIsExporting] = React.useState(false);
  const [showExportConfirmation, setShowExportConfirmation] = React.useState(false);
  const [categoryId, setCategoryId] = React.useState("0");
  const [error, setError] = React.useState(false);

  const handleExport = () => {
    if (exportInProgress) return;
    let data = mergeArrays(selectedItem).map((selectedProduct) => {
      return {
        product_id: selectedProduct,
      };
    });
    setExportInProgress(true);

    WixProduct.export(data, categoryId)
      .then((response: any) => {
        showToast({
          type: "success",
          message: response.message,
        });
        setEmptySelectionItem();
      })
      .catch((error) => {
        showToast({
          type: "error",
          message: error.message,
        });
      })
      .finally(() => {
        setIsExporting(false);
        wixProductQuery.refetch();
      });
  };

  /*
    |--------------------------------------------------------------------------
    | Check If Category Selected
    |--------------------------------------------------------------------------
    |
    */
  const checkCategorySelected = () => {
    if (categoryId !== "0") {
      uploadToFaire();
      return setError(false);
    }
    return setError(true);
  };

  /*
    |--------------------------------------------------------------------------
    | Table Columns
    |--------------------------------------------------------------------------
    |
    */

  const columns: TableColumn<IWixProduct>[] = [
    {
      title: "",
      width: "8%",
      render: (row) => (
        <Layout>
          <Cell span={3}>
            <Box width="80px" height="80px" overflow="hidden">
              {row.thumbnail ? <img src={row.thumbnail} /> : <img src={Picture} />}
            </Box>
          </Cell>
        </Layout>
      ),
    },
    {
      title: "Products",
      width: "22%",
      render: (row) => {
        const skuBlock = row.manageVariants ? (
          <Text size="tiny">{row.sku ? `SKU: ${row.sku}` : ""}</Text>
        ) : !row.variants?.length ? (
          <Text size="tiny">SKU: {row?.sku ?? ""}</Text>
        ) : (
          <Text size="tiny">SKU: {row.variants?.[0]?.sku}</Text>
        );

        return (
          <Layout>
            <Cell span={9}>
              <Tooltip content={row.name}>
                <Text
                  style={{
                    fontSize: "14px ",
                    lineHeight: "18px",
                  }}
                  weight="bold"
                >
                  {truncateText(row.name, 30)}
                </Text>
              </Tooltip>
              <Box margin="8px 0px 5px 0px">{skuBlock}</Box>

              <Badge size="tiny" skin={row.visible ? "success" : "neutralLight"}>
                {row.visible ? "Visible" : "Invisible"}
              </Badge>
            </Cell>
          </Layout>
        );
      },
    },
    {
      title: "Price",
      width: "12%",
      render: (row) => {
        if (!row.variants?.length) {
          return <>{row.formattedPrice}</>;
        }

        const priceBlock = row.manageVariants ? "Price with variants" : row.variants?.[0]?.formattedPrice || "-";

        return <Text size="small">{priceBlock}</Text>;
      },
    },
    {
      title: "Categories",
      width: "18%",
      render: (row) => {
        let collections = (
          <>
            {row.collections.map((collection, index) => (
              <Text light key={index} size="small">
                {index > 0 ? ", " : ""}
                {collection.name}
              </Text>
            ))}
          </>
        );

        return (
          <>
            {row.collections.length > 1 ? (
              <Tooltip className="tooltip_div" content={collections}>
                <Text>{row.collections[0]["name"]}...</Text>
              </Tooltip>
            ) : row.collections.length === 1 ? (
              <Text>{row.collections[0]["name"]}</Text>
            ) : (
              "-"
            )}
          </>
        );
      },
    },
    {
      title: "Inventory",
      width: "10%",
      render: (row) => {
        if (!row.variants?.length) {
          if (row.quantity == 0) {
            return <>In Stock</>;
          } else {
            return <>{row.quantity} in stock</>;
          }
        }

        const quantity = row.variants.reduce((acc, product) => acc + (product?.quantity || 0), 0);

        if (row.manageVariants) {
          return <p>Inventory with variants</p>;
        }

        const firstVariant = row.variants[0];
        const stockStatus = firstVariant?.inStock ? "In stock" : "Out of stock";

        return <>{firstVariant?.trackQuantity ? `${quantity} in stock` : stockStatus}</>;
      },
    },
    {
      title: "View on",
      width: "10%",
      render: (row) => (
        <>
          <Layout>
            <Cell span={4}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  gap: "10px",
                  alignItems: "center",
                }}
              >
                <Tooltip appendTo="window" content="View this product on dashboard">
                  <a href={row.details_url} target="_blank">
                    <img width="32px" height="12px" src={WixLogo} />
                  </a>
                </Tooltip>

                {row.exported_faire_product_id || row.faire_product_id ? (
                  <div
                    style={{
                      width: "50%",
                    }}
                  >
                    <Tooltip appendTo="window" content="View this product on Faire dashboard">
                      <a
                        href={
                          row.exported_faire_product_id
                            ? row.faire_details_url
                            : `https://www.faire.com/brand-portal/my-shop/products/${row.faire_product_id}`
                        }
                        target="_blank"
                      >
                        <img src={FiareLogo} />
                      </a>
                    </Tooltip>
                  </div>
                ) : (
                  <div
                    style={{
                      width: "50%",
                    }}
                  >
                    &nbsp;
                  </div>
                )}
              </div>
            </Cell>
          </Layout>
        </>
      ),
    },
    {
      title: "Action",
      width: "26%",
      render: (row) => {
        return (
          <>
            <Layout>
              <Cell span={6}>
                {row.exported_faire_product_id && row.latest_export_error === null && (
                  <Button
                    size="tiny"
                    skin="destructive"
                    priority="secondary"
                    prefixIcon={<Icons.DeleteSmall />}
                    onClick={() => setProductIdToBeDeletedFromFaire(row.exported_faire_product_id)}
                  >
                    Delete from faire
                  </Button>
                )}
                {row.faire_product_id && row.source === "faire" && (
                  <Tooltip content="This product has been imported from faire">
                    <Box align="center">
                      <Button style={{ backgroundColor: "#7A92A5" }} size="tiny" disabled>
                        <p>Imported</p>
                      </Button>
                    </Box>
                  </Tooltip>
                )}
                {row.is_exporting ? (
                  <Tooltip content="This product is being exported to Faire.">
                    <Box align="center">
                      <Button style={{ backgroundColor: "#7A92A5" }} size="tiny" disabled>
                        <p>Exporting</p>
                      </Button>
                    </Box>
                  </Tooltip>
                ) : (
                  ""
                )}
                {!row.is_exporting &&
                  !row.exported_faire_product_id &&
                  row.source === "wix" &&
                  row.latest_export_error === null && (
                    <Button
                      size="tiny"
                      priority="secondary"
                      onClick={() => handleUploadToFaire(row.productId)}
                      prefixIcon={<Icons.UploadExportSmall />}
                    >
                      Export to Faire
                    </Button>
                  )}

                <Box width="100%" gap={"8px"} align="center" verticalAlign="middle" justifyItems="center">
                  {row.latest_export_error !== null && !row.is_exporting && (
                    <Tooltip content={<>{getExportToFaireErrorMsg(row.latest_export_error)}</>}>
                      <Icons.StatusWarning color="#eb847d" size="2rem" />
                    </Tooltip>
                  )}
                  {row.latest_export_error !== null && !row.is_exporting && (
                    <Tooltip content="Retry Exporting">
                      <Button size="tiny" priority="secondary" onClick={() => handleUploadToFaire(row.productId)}>
                        <Icons.Revert />
                        Retry
                      </Button>
                    </Tooltip>
                  )}
                </Box>
              </Cell>
              {row.manageVariants ? (
                <Cell span={6}>
                  <IconButton
                    skin="standard"
                    priority="secondary"
                    size="tiny"
                    onClick={() => (row.expanded = !row.expanded)}
                  >
                    {row.expanded ? <Icons.ChevronUp /> : <Icons.ChevronDown />}
                  </IconButton>
                </Cell>
              ) : (
                <></>
              )}
            </Layout>
          </>
        );
      },
    },
  ];

  // Handle Wix Products Import
  function handleWixProductsImport() {
    setShowConfirmationForImport(true);
  }

  // Handle Page Change
  function handlePageChange({ page }: { page: number }) {
    setProductPage(page);
  }

  const debounced = useDebouncedCallback(
    function (value) {
      setKeywords(value);
      setProductPage(1);
    },
    1000 // delay in ms
  );

  const wixCollectionOptions = wixCollectionsQuery.isSuccess
    ? addCollectionAndTransform(wixCollectionsQuery.data?.data?.data)
    : [];
  const wixProducts = addExpandProperties(wixProductQuery.data?.data?.data);

  const { setSelectedItem, selectedItem, setItemSelection, selectedItemsCount, setEmptySelectionItem } =
    useTableItemSelection({
      pageNumber: productPage,
      itemLists: wixProducts,
    });

  /*
    |--------------------------------------------------------------------------
    | Import wix products progress implementation
    |--------------------------------------------------------------------------
    |
    | An event will be provided with following data:
    | { progressed_percentage: 25 }. Based on the
    | data, a loader is to be rendered.
    |
    */
  const { siteId } = React.useContext(SiteContext);

  // Import Products Progress
  const { setImportInProgress, importInProgress, setProgress, progress } = usePusherImportProgress({
    siteId: siteId,
    broadcastChannel: `${siteId}-import-wix-products`,
    broadcastAs: "importWixProducts.progressed",
  });

  React.useEffect(() => {
    if (progress === 100) {
      setImportInProgress(false);
      setProgress(0);
      wixProductQuery.refetch();
      wixProductCountRefetch(); // Product Count Refetching
    }
  }, [progress]);

  /*
  |--------------------------------------------------------------------------
  | Bulk Export Progress Implementation 
  |--------------------------------------------------------------------------
  |
  */
  const {
    setExportInProgress,
    exportInProgress,
    setExportProgress,
    exportProgress,
    setBulkExportStatus,
    bulkExportStatus,
    totalProducts,
  } = usePusherBulkExportProgress({
    siteId: siteId,
    broadcastChannel: `${siteId}-bulk-export-wix-products`,
    broadcastAs: "bulkExportWixProducts.progressed",
  });

  React.useEffect(() => {
    if (totalProducts == exportProgress && bulkExportStatus === "complete") {
      setExportInProgress(false);
      setExportProgress(0);
      wixProductQuery.refetch();
      wixProductCountRefetch();
      setBulkExportStatus("");
      setEmptySelectionItem();
    }
  }, [bulkExportStatus]);

  /*
  |--------------------------------------------------------------------------
  | Product Export Progress Implementation
  |--------------------------------------------------------------------------
  |
  */
  const { exportProductInProgress, setExportProductInProgress, exportProductStatus } = usePusherExportProgress({
    siteId: siteId,
    broadcastChannel: `${siteId}-wix-product-export-progress`,
    broadcastAs: "wixProductExport.progress",
  });

  React.useEffect(() => {
    setExportProductInProgress(false);
    wixProductQuery.refetch();

    if (exportProductStatus === "complete" && exportProductInProgress === false) {
      setExportedData((prevData: any) => prevData + 1);
      setNotExportedData((previousData: any) => previousData - 1);
    }
  }, [exportProductInProgress]);

  /*
    |--------------------------------------------------------------------------
    | Actions toolbar
    |--------------------------------------------------------------------------
    |
    */
  const renderActionsToolbar = () => {
    return (
      <>
        <Divider direction="horizontal" />
        <TableToolbar>
          <TableToolbar.Item layout="button">
            <Button
              size="small"
              disabled={exportInProgress}
              prefixIcon={
                exportInProgress ? (
                  <span>
                    <Loader size="tiny" />
                  </span>
                ) : (
                  <Icons.UploadExportSmall />
                )
              }
              onClick={() => setShowExportConfirmation(true)}
            >
              {exportInProgress ? (
                <Text skin="standard" weight="bold">
                  Exporting {exportProgress}/{totalProducts} products
                </Text>
              ) : (
                `Export ${selectedItemsCount} product(s) to Faire`
              )}
            </Button>
          </TableToolbar.Item>
        </TableToolbar>
      </>
    );
  };

  const renderRowDetails = useMemo(() => {
    return (row: IWixProduct) => {
      if (row.expandable) {
        return <WixProductRowDetail product={row} />;
      }
    };
  }, []);

  const selectedCategory = faireCategoryOptions.find((item) => item.id === categoryId);
  const similarSkuExistProps = { selectedProductId, showConirmationForUploadToFaire, wixProducts };

  return (
    <WixDesignSystemProvider>
      <Page height="95vh">
        <Page.Header
          title={
            <Box gap={"10px"} align="space-between" verticalAlign="middle">
              Products <SyncedDate source="wix" />
            </Box>
          }
          subtitle={
            <Text size="tiny">
              Here is a list of your products in your store. You can select the products you want to export to Faire.
              ‘Imported' badge can be seen under the actions column for the products that have been imported from Faire
              to WIX store. Please use the filters below to see products you have already exported or to see products
              based on status.
            </Text>
          }
          actionsBar={
            <Box direction="vertical" align="center" marginTop={"0px"} marginLeft={"20px"}>
              <Tooltip appendTo="window" content="Import all your store products into the app.">
                <Button
                  onClick={() => setShowConfirmationForImport(true)}
                  prefixIcon={
                    importInProgress ? (
                      <span className="import_progress_loader_margin">
                        <Loader size="tiny" />
                      </span>
                    ) : (
                      <Icons.Download />
                    )
                  }
                  disabled={importInProgress}
                >
                  {importInProgress ? (   
                    <Text skin="standard" weight="bold">
                      Importing products ... {progress}%
                    </Text>
                  ) : (
                    "Import Products"
                  )}
                </Button>
              </Tooltip>
            </Box>
          }
        />
        <Page.Content>
          <TrialExpiryAlert />
          {/* Delete From Faire Confirmation Modal */}
          <Modal
            isOpen={showDeleteFromFaireConfirmation}
            shouldCloseOnOverlayClick={true}
            onRequestClose={() => {
              setProductIdToBeDeletedFromFaire("");
              setShowDeleteFromFaireConfirmation(false);
            }}
          >
            <MessageModalLayout
              onCloseButtonClick={() => {
                setProductIdToBeDeletedFromFaire("");
                setShowDeleteFromFaireConfirmation(false);
              }}
              primaryButtonText="Yes"
              primaryButtonOnClick={() => {
                setShowDeleteFromFaireConfirmation(false);
                deleteFromFaire();
              }}
              secondaryButtonText="Cancel"
              secondaryButtonOnClick={() => {
                setProductIdToBeDeletedFromFaire("");
                setShowDeleteFromFaireConfirmation(false);
              }}
              title="Delete from faire"
              content={<Text>Are you sure you want to delete this product from Faire?</Text>}
            />
          </Modal>

          {/* Export Modal */}
          <ExportModal
            title="Export wix to faire"
            subtitle="All Selected product will be send to the selected category in Faire"
            source="wix"
            showExportConfirmation={showExportConfirmation}
            setShowExportConfirmation={() => setShowExportConfirmation(false)}
            handleExport={() => {
              setShowExportConfirmation(false);
              handleExport();
              setSelectedItem({});
            }}
            hasMore={hasMore}
            loadMore={fetchCategories}
            options={faireCategoryOptions}
            onSelect={(category: any) => {
              setCategoryId(category.id);
              setCategoryKeywords(category.value);
            }}
            onChange={(event: any) => {
              setCategoryKeywords(event.target.value);
              debouncedCategory(event.target.value);
            }}
            selectedProducts={mergeArrays(selectedItem)}
            setCategoryId={setCategoryId}
            categoryId={categoryId}
            value={categoryKeywords}
            placeholder={"Select Category"}
            onAfterClose={() => {
              setCategoryKeywords("");
            }}
          />

          {/* Upload To Faire Confirmation Modal */}
          <Modal
            isOpen={showConirmationForUploadToFaire}
            onRequestClose={() => setShowConfirmationForUploadToFaire(false)}
            shouldCloseOnOverlayClick={!uploadingToFaire}
            onAfterClose={() => {
              setCategoryId("0");
              setCategoryKeywords("");
              handleCancelUploadToFaire();
              setError(false);
            }}
            screen="desktop"
          >
            <CustomModalLayout
              primaryButtonProps={{ disabled: uploadingToFaire }}
              secondaryButtonProps={{ disabled: uploadingToFaire }}
              onCloseButtonClick={
                !uploadingToFaire
                  ? () => {
                      handleCancelUploadToFaire();
                      setShowConfirmationForUploadToFaire(false);
                    }
                  : undefined
              }
              primaryButtonText={uploadingToFaire ? "Upload in progress" : "Yes"}
              primaryButtonOnClick={() => {
                handleConfirmUploadToFaire();
              }}
              secondaryButtonText="Cancel"
              secondaryButtonOnClick={() => {
                setShowConfirmationForUploadToFaire(false);
              }}
              title="Upload to Faire"
              sideActions={<SimilarSkuExists {...similarSkuExistProps} />}
              subtitle=" Are you sure you want to upload this product to Faire?"
              overflowY="none"
              content={
                <FormField label="The product will be send to the selected category in Faire">
                  <AutoComplete
                    dropdownWidth="80px"
                    popoverProps={{
                      appendTo: "scrollParent",
                      dynamicWidth: true,
                    }}
                    size="small"
                    border="round"
                    focusOnSelectedOption
                    selectedId={categoryId}
                    onSelect={(category: any) => {
                      setCategoryId(category.id);
                      setCategoryKeywords(category.value);
                    }}
                    placeholder={selectedCategory ? selectedCategory.value : "Select Category"}
                    infiniteScroll
                    hasMore={hasMore}
                    loadMore={fetchCategories}
                    options={options}
                    onChange={(event: any) => {
                      setCategoryKeywords(event.target.value);
                      debouncedCategory(event.target.value);
                    }}
                    value={categoryKeywords}
                  />
                  <Text skin="error" size="small">
                    {error && "*Category is required."}
                  </Text>
                </FormField>
              }
            />
          </Modal>

          {/* Import Wix Products Confirmation Modal */}
          <Modal isOpen={showConfirmationForImport} onRequestClose={() => setShowConfirmationForImport(false)}>
            <MessageModalLayout
              onCloseButtonClick={() => setShowConfirmationForImport(false)}
              primaryButtonText="Yes"
              primaryButtonOnClick={() => {
                wixProductsImportMutation.mutate();
                setShowConfirmationForImport(false);
              }}
              secondaryButtonText="Cancel"
              secondaryButtonOnClick={() => setShowConfirmationForImport(false)}
              title="Import Wix Products"
              content={<Text>Are you sure you want to import Wix products?</Text>}
            />
          </Modal>

          <Card className="wix_product_table">
            <Table
              data={wixProducts}
              columns={columns}
              rowDetails={(row) => renderRowDetails(row)}
              showSelection
              selectionDisabled={(row) =>
                (row.faire_product_id || row.exported_faire_product_id || row.is_exporting) && true
              }
              onSelectionChanged={setItemSelection}
              selectedIds={selectedItem[productPage]}
            >
              <Page.Sticky>
                <Card>
                  <TableToolbar removeVerticalPadding>
                    <TableToolbar.ItemGroup>
                      <Tabs
                        size="medium"
                        hasDivider={false}
                        items={productStatusOptions}
                        activeId={exportStatus}
                        onClick={(value) => handleTabChange(value)}
                      />
                    </TableToolbar.ItemGroup>

                    <TableToolbar.ItemGroup>
                      <TableToolbar.Item>
                        <Box direction="horizontal" gap={"SP2"}>
                          {(collectionId || visibility) && (
                            <Button
                              size="small"
                              priority="secondary"
                              prefixIcon={<Icons.Dismiss />}
                              onClick={() => {
                                setCollectionId("");
                                setVisibility("");
                              }}
                            >
                              Clear
                            </Button>
                          )}

                          <Button
                            size="small"
                            priority="secondary"
                            prefixIcon={<Icons.ContentFilterSmall />}
                            onClick={openPanel}
                          >
                            Filter
                          </Button>
                        </Box>
                      </TableToolbar.Item>

                      <TableToolbar.Item>
                        <Search
                          placeholder="Product name/SKU"
                          size="small"
                          value={keywords}
                          onChange={(e) => debounced(e.target.value)}
                          onClear={(e) => setKeywords("")}
                        />
                      </TableToolbar.Item>
                    </TableToolbar.ItemGroup>
                  </TableToolbar>
                  <Table.ToolbarContainer>
                    {() => (selectedItemsCount > 0 || exportInProgress ? renderActionsToolbar() : "")}
                  </Table.ToolbarContainer>
                  <Table.Titlebar />
                </Card>
              </Page.Sticky>
              <Card>
                {wixProductQuery.isLoading || wixProductQuery.isFetching ? (
                  <Loading title="Fetching Wix Products" />
                ) : (
                  <>
                    {wixProductQuery.isSuccess && wixProducts?.length ? (
                      <Table.Content titleBarVisible={false} />
                    ) : (
                      <EmptyTable
                        hasKeywords={keywords.length > 0}
                        onChange={() => setKeywords("")}
                        title="No products found"
                        description="Couldn't find any products. Try adding new products or refine your filter."
                        actions={[
                          <Button
                            size="small"
                            priority="secondary"
                            prefixIcon={<Icons.Add />}
                            onClick={handleWixProductsImport}
                          >
                            Import products
                          </Button>,
                        ]}
                      />
                    )}
                  </>
                )}
              </Card>
            </Table>
          </Card>
          {wixProductQuery.isSuccess && (
            <Layout justifyItems="center">
              <Cell>
                <Box marginTop="24px">
                  <Pagination
                    currentPage={productPage}
                    totalPages={wixProductQuery.data?.data?.last_page}
                    onChange={handlePageChange}
                  />
                </Box>
              </Cell>
            </Layout>
          )}
        </Page.Content>
      </Page>

      {/* Side Panel Modal */}
      <SidePanelModal
        closePanel={closePanel}
        showSidePanel={showSidePanel}
        setShowSidePanel={setShowSidePanel}
        items={[
          accordionItemBuilder({
            title: "All Statuses",
            children: (
              <Box direction="vertical" width="100%">
                <Dropdown
                  size="small"
                  border="round"
                  options={visibilityStatusOptions}
                  selectedId={visibility}
                  onSelect={(status) => {
                    setVisibility(status.id);
                    setProductPage(1);
                  }}
                />
              </Box>
            ),
          }),
          // accordionItemBuilder({
          //   title: "Categories",
          //   children: (
          //     <Box width="100%" direction="vertical">
          //       <Dropdown
          //         size="small"
          //         border="round"
          //         options={wixCollectionOptions}
          //         selectedId={collectionId}
          //         onSelect={(collection) => {
          //           setCollectionId(collection.id);
          //           setProductPage(1);
          //         }}
          //       />
          //     </Box>
          //   ),
          // }),
        ]}
      />
    </WixDesignSystemProvider>
  );
};

export default withProviders(Index);
